import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Connects to data-controller="unsaved-changes"
export default class extends Controller {
  static targets = ["dialog"]
  static values = { initiallyDirty: { type: Boolean, default: false } }

  connect() {
    this.dirty = this.initiallyDirtyValue
    this.submitting = false
    this.intentToSubmit = false
    this.pendingNavigation = null

    this.markDirty = this.markDirty.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onBeforeVisit = this.onBeforeVisit.bind(this)
    this.onSubmitStart = this.onSubmitStart.bind(this)
    this.onSubmitEnd = this.onSubmitEnd.bind(this)
    this.onFormSubmit = this.onFormSubmit.bind(this)
    this.onBeforeUnload = this.onBeforeUnload.bind(this)
    this.onBeforeStreamRender = this.onBeforeStreamRender.bind(this)

    this.element.addEventListener("input", this.markDirty)
    this.element.addEventListener("change", this.markDirty)
    this.element.addEventListener("trix-change", this.markDirty)
    this.element.addEventListener("submit", this.onFormSubmit, true)
    this.element.addEventListener("turbo:submit-start", this.onSubmitStart)
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)

    document.addEventListener("click", this.onClick, true)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    document.addEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    window.addEventListener("beforeunload", this.onBeforeUnload)
  }

  disconnect() {
    this.element.removeEventListener("input", this.markDirty)
    this.element.removeEventListener("change", this.markDirty)
    this.element.removeEventListener("trix-change", this.markDirty)
    this.element.removeEventListener("submit", this.onFormSubmit, true)
    this.element.removeEventListener("turbo:submit-start", this.onSubmitStart)
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    document.removeEventListener("click", this.onClick, true)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    document.removeEventListener("turbo:before-stream-render", this.onBeforeStreamRender)
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.body.classList.remove("overflow-hidden")
  }

  markDirty() {
    if (!this.submitting) this.dirty = true
  }

  primaryForm() {
    return this.element.querySelector("form")
  }

  isPrimaryForm(form) {
    return form instanceof HTMLFormElement && form === this.primaryForm()
  }

  onFormSubmit(event) {
    if (!this.isPrimaryForm(event.target)) return

    this.submitting = true
    this.intentToSubmit = false
    this.dirty = false
  }

  onSubmitStart(event) {
    if (this.isPrimaryForm(event.target)) this.submitting = true
  }

  onSubmitEnd(event) {
    if (this.isPrimaryForm(event.target)) {
      this.submitting = false
      if (event.detail?.success) this.dirty = false
    } else if (this.element.contains(event.target) && event.detail?.success) {
      this.dirty = true
    }
  }

  onBeforeStreamRender(event) {
    if (this.submitting || event.target.getAttribute("target") !== "current_treatments") return

    const target = document.getElementById("current_treatments")
    if (target && this.element.contains(target)) this.dirty = true
  }

  onClick(event) {
    const submit = event.target.closest("button[type='submit'], input[type='submit']")
    if (submit && this.element.contains(submit)) {
      if (this.isPrimaryForm(submit.form)) {
        this.intentToSubmit = true
        setTimeout(() => { this.intentToSubmit = false }, 100)
      }
      return
    }

    if (!this.dirty || this.submitting) return

    const link = event.target.closest("a[href]")
    if (!link) return

    const href = link.getAttribute("href")
    if (!href || href === "#") return
    if (link.dataset.turbo === "false") return
    if (this.element.contains(link) && this.isInPageLink(link)) return

    event.preventDefault()
    event.stopImmediatePropagation()

    this.pendingNavigation = { type: "link", link }
    this.showDialog()
  }

  isInPageLink(link) {
    if (link.dataset.turboMethod) return true

    const frame = link.dataset.turboFrame
    return frame && frame !== "_top" && frame !== "patient_content"
  }

  onBeforeVisit(event) {
    if (!this.dirty || this.submitting) return

    event.preventDefault()
    this.pendingNavigation = { type: "visit", url: event.detail.url }
    this.showDialog()
  }

  onBeforeUnload(event) {
    if (!this.dirty || this.submitting || this.intentToSubmit) return

    event.preventDefault()
    event.returnValue = ""
  }

  showDialog() {
    this.dialogTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  hideDialog() {
    this.dialogTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  stay() {
    this.pendingNavigation = null
    this.hideDialog()
  }

  leave() {
    this.dirty = false
    this.hideDialog()

    const pending = this.pendingNavigation
    this.pendingNavigation = null
    if (!pending) return

    if (pending.type === "link") {
      pending.link.click()
    } else {
      Turbo.visit(pending.url)
    }
  }
}
